'use client';

import { useQuery } from 'convex-helpers/react/cache/hooks';
import { api } from 'convex/_generated/api';
import type { Id } from 'convex/_generated/dataModel';
import { findGardenById } from '@modules/my-gardens/business_logic';
import { useDashboardStore } from '@shared/store/useDashboardStore';
import { DateWeatherWidgetContainer } from '@modules/dashboard/containers/date_weather_widget_container';
import type { DateWeatherWidgetProps } from '@modules/dashboard/containers/date_weather_widget_container';
import { SeedWidgetContainer } from '@modules/dashboard/containers/seed_widget_container';
import type { SeedWidgetProps } from '@modules/dashboard/containers/seed_widget_container';
import type { Garden, Plant } from '@modules/dashboard/business_logic';
import { ComponentType, useEffect } from 'react';

export interface DashboardProps {
  isLoading: boolean;
  gardens: Garden[];
  plants: Plant[];
  selectedGarden: Garden | null;
  selectedGardenId: Id<'gardens'> | null;
  onGardenChange: (id: Id<'gardens'> | null) => void;
  dateWeatherWidget: React.ReactNode;
  seedWidget: React.ReactNode;
}

interface DashboardContainerProps {
  View: ComponentType<DashboardProps>;
  DateWeatherWidgetView: ComponentType<DateWeatherWidgetProps>;
  SeedWidgetView: ComponentType<SeedWidgetProps>;
}

export function DashboardContainer({
  View,
  DateWeatherWidgetView,
  SeedWidgetView,
}: DashboardContainerProps) {
  const gardens = useQuery(api.gardens.queries.getUserGardens, {});
  const plants = useQuery(api.plants.queries.getAllPlants, {});

  const selectedGardenId = useDashboardStore((s) => s.selectedGardenId);
  const setSelectedGardenId = useDashboardStore((s) => s.setSelectedGardenId);

  useEffect(() => {
    if (!selectedGardenId && gardens && gardens.length > 0) {
      setSelectedGardenId(gardens[0]._id);
    }
  }, [selectedGardenId, gardens, setSelectedGardenId]);

  const selectedGarden = gardens !== undefined ? findGardenById(gardens, selectedGardenId) : null;

  const isLoading = gardens === undefined || plants === undefined;

  return (
    <View
      isLoading={isLoading}
      gardens={gardens ?? []}
      plants={plants ?? []}
      selectedGarden={selectedGarden}
      selectedGardenId={selectedGardenId}
      onGardenChange={setSelectedGardenId}
      dateWeatherWidget={<DateWeatherWidgetContainer View={DateWeatherWidgetView} />}
      seedWidget={
        <SeedWidgetContainer
          View={SeedWidgetView}
          selectedGarden={selectedGarden}
          plants={plants ?? []}
        />
      }
    />
  );
}
